import { motion } from "framer-motion";
import { DollarSign, Wrench, TrendingUp, Heart } from "lucide-react";

const benefits = [
  {
    icon: DollarSign,
    title: "Revenue Share",
    description: "Earn up to £3,600 monthly from each pod installation.",
  },
  {
    icon: Wrench,
    title: "Zero Setup Cost",
    description: "We handle all installation and maintenance expenses.",
  },
  {
    icon: TrendingUp,
    title: "Customer Growth",
    description: "Attract new customers and increase retention.",
  },
  {
    icon: Heart,
    title: "Full Support",
    description: "24/7 technical support and maintenance included.",
  },
];

const PartnerCTA = () => {
  return (
    <section id="partner-section" className="w-full bg-[#1f3d32] py-32 overflow-hidden relative">
      {/* Gradient Flare Background */}
      <div 
        className="absolute inset-0 opacity-60 mix-blend-screen pointer-events-none"
        style={{
          background: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 40%),
            linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.05) 50%, transparent 70%)
          `
        }}
      />
      
      <div className="container px-4 mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-funnel text-4xl tablet:text-5xl laptop:text-6xl desktop:text-6xl font-bold text-white mb-6">
            Want to host a pod at<br />your space?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed font-funnel">
            If you run a cafe, fitness club, hotel, transit hub or shared space and have a spare 1m x 1m footprint, qwip lets you generate additional income with zero setup or maintenance on your side.
          </p>
          <a
            href="https://unequaled-engine-3ab.notion.site/1580a75a9cdf8055aa7de5da547445ca"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 bg-white text-[#1f3d32] rounded-full font-semibold text-base hover:bg-white/90 transition-colors shadow-lg"
          >
            Become a Partner
          </a>

          {/* Questions row */}
          <div className="mt-6 flex flex-col items-center gap-2">
            <p className="text-white/60 text-sm">Have any questions?</p>
            <div className="flex flex-col items-center gap-2">
              <a
                href="https://wa.me/447347474059"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full text-sm font-medium transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
              <a
                href="mailto:hello@qwip.co.uk"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full text-sm font-medium transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                hello@qwip.co.uk
              </a>
            </div>
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-4 desktop:grid-cols-4 gap-6 mt-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-funnel text-lg font-bold text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerCTA;